import React from "react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import {
  ChatIcon,
  ConnectivityIcon,
  HandIcon,
  PersonIcon,
  PoorConnectivityIcon,
} from "@100mslive/react-icons";
import { Button } from "@100mslive/roomkit-react";
import {
  useIsSidepaneTypeOpen,
  useSidepaneToggle,
} from "../AppData/useSidepane";
import { SIDE_PANE_OPTIONS } from "../../common/constants";

const ChatAction = React.forwardRef((_, ref) => {
  const toggleChat = useSidepaneToggle(SIDE_PANE_OPTIONS.CHAT);
  const isChatOpen = useIsSidepaneTypeOpen(SIDE_PANE_OPTIONS.CHAT);
  const { t } = useTranslation();

  if (isChatOpen) {
    return null;
  }

  return (
    <Button
      outlined
      as="div"
      variant="standard"
      css={{ w: "max-content" }}
      onClick={toggleChat}
      ref={ref}
    >
      {t("lbl_open_chat")}
    </Button>
  );
});

export const ToastConfig = {
  PEER_LIST: {
    single: function (notification) {
      if (notification.data.length === 1) {
        return {
          title: i18next.t("fm_joined", { name: notification.data[0]?.name }),
          icon: <PersonIcon />,
        };
      }
      return {
        title: i18next.t("fm_others_joined", {
          name: notification.data[notification.data.length - 1]?.name,
          number: notification.data.length - 1,
        }),
        icon: <PersonIcon />,
      };
    },
    multiple: notifications => {
      return {
        title: i18next.t("fm_others_joined", {
          name: notifications[0].data.name,
          number: notifications.length - 1,
        }),
        icon: <PersonIcon />,
      };
    },
  },
  PEER_JOINED: {
    single: function (notification) {
      return {
        title: i18next.t("fm_joined", { name: notification.data?.name }),
        icon: <PersonIcon />,
      };
    },
    multiple: function (notifications) {
      return {
        title: i18next.t("fm_others_joined", {
          name: notifications[notifications.length - 1].data.name,
          number: notifications.length - 1,
        }),
        icon: <PersonIcon />,
      };
    },
  },
  PEER_LEFT: {
    single: function (notification) {
      return {
        title: i18next.t("fm_left", { name: notification.data?.name }),
        icon: <PersonIcon />,
      };
    },
    multiple: function (notifications) {
      return {
        title: i18next.t("fm_others_left", {
          name: notifications[notifications.length - 1].data.name,
          number: notifications.length - 1,
        }),
        icon: <PersonIcon />,
      };
    },
  },
  METADATA_UPDATED: {
    single: notification => {
      return {
        title: i18next.t("fm_raised_hand", { name: notification.data?.name }),
        icon: <HandIcon />,
      };
    },
    multiple: notifications => {
      return {
        title: i18next.t("fm_others_raised_hand", {
          name: notifications[notifications.length - 1].data?.name,
          number: notifications.length - 1,
        }),
        icon: <HandIcon />,
      };
    },
  },
  NEW_MESSAGE: {
    single: notification => {
      return {
        title: i18next.t("fm_new_message", {
          name: notification.data?.senderName,
        }),
        icon: <ChatIcon />,
        action: <ChatAction />,
      };
    },
    multiple: notifications => {
      return {
        title: i18next.t("fm_new_message_multiple", {
          number: notifications.length,
        }),
        icon: <ChatIcon />,
        action: <ChatAction />,
      };
    },
  },
  RECONNECTED: {
    single: () => {
      return {
        title: i18next.t("fm_reconnected"),
        icon: <ConnectivityIcon />,
        variant: "success",
        duration: 3000,
      };
    },
  },
  RECONNECTING: {
    single: message => {
      return {
        title: i18next.t("fm_reconnecting", { message }),
        icon: <PoorConnectivityIcon />,
        variant: "warning",
        duration: 30000,
      };
    },
  },
};
